import { Box, Button, FormControl, FormHelperText, FormLabel, Typography } from '@material-ui/core';
import { makeStyles, Theme, createStyles } from '@material-ui/core/styles';
import PhotoLibraryIcon from '@material-ui/icons/PhotoLibrary';
import { useField } from 'formik';
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAlert } from '../../../components/AlertProvider';
import useCommonStyles from '../../../useCommonStyles';
import { getAcceptExtensions, getExtension } from '../../../utilities/file';
import useCompanySetting from '../../../utilities/useCompanySetting';

type SettingsLogoFieldProps = {
    name: string;
    labelKey: string;
    emptyLabelKey: string;
};

const defaultAllowedLogoExtensions = ['jpg', 'jpeg', 'png'];

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        logoPreview: {
            width: theme.spacing(18),
            height: theme.spacing(18),
            objectFit: 'contain',
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(1),
            backgroundColor: theme.palette.common.white,
        },
        logoPlaceholder: {
            width: theme.spacing(18),
            height: theme.spacing(18),
            border: `1px dashed ${theme.palette.divider}`,
            borderRadius: theme.shape.borderRadius,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textAlign: 'center',
            padding: theme.spacing(2),
        },
        uploadButton: {
            marginTop: theme.spacing(2),
        },
    })
);

const SettingsLogoField = ({ name, labelKey, emptyLabelKey }: SettingsLogoFieldProps) => {
    const { t } = useTranslation(['settingsPage', 'common', 'errors']);
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const { show } = useAlert();
    const [field, meta, { setValue, setTouched }] = useField<File | string | null>(name);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const {
        companySetting: { fileSettings },
    } = useCompanySetting();
    const maxAllowedSize = fileSettings.maxUploadFileSize * 1000 * 1000;
    const allowedExtensions = fileSettings.allowedImageFileExtensions;
    const [previewUrl, setPreviewUrl] = useState<string | undefined>();

    useEffect(() => {
        if (!field.value) {
            setPreviewUrl(undefined);

            return () => undefined;
        }

        if (!(field.value instanceof File)) {
            setPreviewUrl(field.value);

            return () => undefined;
        }

        const objectUrl = URL.createObjectURL(field.value);
        setPreviewUrl(objectUrl);

        return () => {
            URL.revokeObjectURL(objectUrl);
        };
    }, [field.value]);

    const onChange = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const file = event.currentTarget.files?.[0];

            if (!file) {
                return;
            }

            if (file.size > maxAllowedSize) {
                show('error', t('errors:largeFileSize', { fileSize: maxAllowedSize / 1000 / 1000 }));

                return;
            }

            const extension = getExtension(file.name)?.toLowerCase();

            if (!extension || !allowedExtensions.includes(extension)) {
                show('error', t('errors:invalidFileType'));

                return;
            }

            setTouched(true, false);
            setValue(file);
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';
        },
        [allowedExtensions, maxAllowedSize, setTouched, setValue, show, t]
    );

    const accept = useMemo(
        () =>
            getAcceptExtensions(
                allowedExtensions,
                fileSettings?.allowedImageFileExtensions ?? defaultAllowedLogoExtensions,
                fileSettings?.allowedDocFileExtensions ?? []
            ).join(','),
        [allowedExtensions, fileSettings?.allowedDocFileExtensions, fileSettings?.allowedImageFileExtensions]
    );

    const hasError = !!meta.error && meta.touched;

    return (
        <FormControl fullWidth>
            <FormLabel className={commonStyles.formLabel}>{t(labelKey)}</FormLabel>
            <Box alignItems="flex-start" display="flex" flexDirection="column">
                {previewUrl ? (
                    <img alt={t(labelKey)} className={styles.logoPreview} src={previewUrl} />
                ) : (
                    <Box className={styles.logoPlaceholder}>
                        <Typography color="textSecondary" variant="body2">
                            {t(emptyLabelKey)}
                        </Typography>
                    </Box>
                )}
                <Button
                    className={styles.uploadButton}
                    color="secondary"
                    onClick={() => fileInputRef.current?.click()}
                    startIcon={<PhotoLibraryIcon />}
                    variant="outlined"
                >
                    {t('common:upload')}
                </Button>
                <input ref={fileInputRef} accept={accept} onChange={onChange} style={{ display: 'none' }} type="file" />
            </Box>
            <FormHelperText error={hasError}>{hasError ? meta.error : ''}</FormHelperText>
        </FormControl>
    );
};

export default SettingsLogoField;
